/* @flow */
import { Request } from "shared/src/services/Requests";
import { AnonCartItemPrices } from "shared/src/types/CartPrice";

const RequestBuilder = new Request.Builder("ordercapture", "anonymouscart", true);

export type CartContext = {
    country: string;
    storeId: string;
    locale: string;
    anonUserId: string;
};

type GetCartRequest = {
    cartContext: CartContext;
    cartId: string;
};

type AddItemRequest = {
    requestId: string;
    cartContext: CartContext;
    cartId: string;
    bundleId: string;
    quantity: number;
};

type SetItemQuantityRequest = {
    requestId: string;
    cartContext: CartContext;
    cartId: string;
    bundleId: string;
    quantity: number;
};

export type AnonCart = {
    cartId: string;
    cartVersion: number;
    items: {
        [bundleId: string]: AnonCartItem;
    };
    eventHistory: Array<string>;
};

export type AnonCartItem = {
    bundleId: string;
    quantity: number;
    prices: AnonCartItemPrices | undefined | null;
    errors: AnonCartItemError[];
};

export type AnonCartItemError = {
    readonly reason: string;
};

export type CartEvent = {
    type: string;
    eventId: string;
    seqNum: number;
    bundleId: string | undefined | null;
    quantity: number | undefined | null;
    status: {
        type: "Success" | "Failure";
    };
    prices:
        | {
              articlePrices: {
                  [x: string]: AnonCartItemPrices;
              };
          }
        | undefined
        | null;
};

function getQueryParams(context: CartContext): {
    [x: string]: any;
} {
    return {
        country: context.country,
        storeId: context.storeId,
        anonUserId: context.anonUserId
    };
}

export function getCartVersion(params: GetCartRequest): Promise<number | undefined | null> {
    const template = "/anon-carts/{cartId}/version";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);

    return RequestBuilder.path(template, pathParams)
        .query(queryParams)
        .get()
        .then((json) => json.data);
}

export function getCart(params: GetCartRequest): Promise<AnonCart | undefined | null> {
    const template = "/anon-carts/{cartId}";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);

    return RequestBuilder.path(template, pathParams)
        .query(queryParams)
        .get()
        .then((json) => json.data);
}

export function addItem(params: AddItemRequest): Promise<void> {
    const template = "/anon-carts/{cartId}/items";
    const pathParams = { cartId: params.cartId };
    const queryParams = getQueryParams(params.cartContext);
    const body = {
        requestId: params.requestId,
        bundleId: params.bundleId,
        quantity: params.quantity
    };
    return RequestBuilder.path(template, pathParams).query(queryParams).body(body).post();
}

export function setItemQuantity(params: SetItemQuantityRequest): Promise<void> {
    const template = "/anon-carts/{cartId}/items/{bundleId}/quantity";
    const pathParams = { cartId: params.cartId, bundleId: params.bundleId };
    const queryParams = getQueryParams(params.cartContext);
    const body = {
        requestId: params.requestId,
        quantity: params.quantity
    };
    return RequestBuilder.path(template, pathParams).query(queryParams).body(body).put();
}
